import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {HydratedAPITask} from '../types/api-task'
import {ConnectionStatus, TERMINAL_SESSION_STATES, isSessionState} from '../types/session'
import type {Session} from '../types/session'
import type {BaseTask, UITask} from '../types/task'
import {COPILOT_CLI_SLUG, COPILOT_VSCODE_SLUG} from './agent-helpers'
import {getAPITaskState} from './task-helpers'

/**
 * Slugs that behave like CLI sessions (remotable agents).
 */
const CLI_SLUGS: ReadonlySet<string> = new Set([COPILOT_CLI_SLUG, COPILOT_VSCODE_SLUG])

/**
 * Checks if a slug belongs to a CLI-like (remotable) agent.
 */
export function isCLIAgentSlug(slug: string | undefined): boolean {
  return !!slug && CLI_SLUGS.has(slug)
}

/**
 * Checks if a session belongs to the Copilot CLI agent.
 * Uses slug-based detection via the agent_slug field on the session.
 */
export function isCLISession(session: Session | null | undefined): boolean {
  if (!session) return false
  return isCLIAgentSlug(session.agent_slug)
}

/**
 * Checks if a task was created by the Copilot CLI.
 * Uses slug-based detection.
 */
export function isTaskFromCLI(task: UITask | null | undefined): boolean {
  if (!task) return false
  return isCLIAgentSlug(task.agentSlug)
}

/**
 * Checks if an API task was created by the Copilot CLI.
 * Uses slug-based detection.
 */
export function isAPITaskFromCLI(task: HydratedAPITask | null | undefined): boolean {
  if (!task) return false
  return task.agent_collaborators?.some(c => isCLIAgentSlug(c.slug)) ?? false
}

/**
 * Computes the connection status for a task based on session state and stale_at deadline.
 *
 * Priority:
 * 1. Terminal session state (completed/failed/timed_out/cancelled) → always Disconnected.
 * 2. stale_at deadline → Connected if in the future, Disconnected if in the past or missing.
 *
 * When `dataUpdatedAt` is provided: if the stale_at deadline has passed but
 * data was last fetched *before* that deadline, we keep the status as Connected
 * because the server hasn't had a chance to provide a fresh stale_at value.
 *
 * Returns undefined if the task is not a CLI task or the feature is disabled.
 */
export function computeTaskConnectionStatus(task: UITask, dataUpdatedAt?: number): ConnectionStatus | undefined {
  if (!isTaskFromCLI(task)) return undefined
  if (!isFeatureEnabled('copilot_mission_control_cli_session_status')) return undefined

  if (TERMINAL_SESSION_STATES.has(task.latest_session_state)) {
    return ConnectionStatus.Disconnected
  }

  if (!task.stale_at) return ConnectionStatus.Disconnected

  const staleAt = new Date(task.stale_at).getTime()
  if (Date.now() < staleAt) return ConnectionStatus.Connected

  // Deadline has passed — but if our data is older than the deadline,
  // we don't yet have a server response that reflects the post-deadline state.
  // Additionally, because we have a `stale_at` and aren't in a terminal state,
  // we can assume that the session is online.
  if (dataUpdatedAt !== undefined && dataUpdatedAt < staleAt) {
    return ConnectionStatus.Connected
  }

  return ConnectionStatus.Disconnected
}

/**
 * Interval at which to poll for fresh task data when CLI sessions are active.
 * This is necessary because `stale_at` is a computed property — it's calculated
 * when the API response is constructed, not stored in the database. Since Alive
 * events only fire on database writes, `stale_at` never triggers an Alive update.
 */
export const CLI_POLL_INTERVAL_MS = 20_000

/**
 * Returns the CLI polling interval when `shouldPoll` is true and the
 * `copilot_mission_control_cli_session_status` feature flag is enabled.
 * Returns `false` otherwise. Intended for use as a `refetchInterval` value
 * (or inside a `refetchInterval` callback) on a React Query query.
 */
export function getCliRefetchInterval(shouldPoll: boolean): number | false {
  if (!isFeatureEnabled('copilot_mission_control_cli_session_status')) return false
  return shouldPoll ? CLI_POLL_INTERVAL_MS : false
}

/**
 * Returns true when any task in the list was created by the Copilot CLI.
 */
export function hasCliTasks(tasks: Array<Pick<BaseTask, 'agentSlug'>>): boolean {
  return tasks.some(task => isCLIAgentSlug(task.agentSlug))
}

/**
 * Returns true when the given API task is a CLI session that has not yet
 * reached a terminal state (completed, failed, timed_out, cancelled).
 * Unknown or unexpected states are treated as non-terminal to avoid
 * silently stopping polling.
 */
export function isNonTerminalCliAPITask(task: HydratedAPITask | null | undefined): boolean {
  if (!task || !isAPITaskFromCLI(task)) return false
  const state = getAPITaskState(task)
  return !isSessionState(state) || !TERMINAL_SESSION_STATES.has(state)
}
